import React from 'react';
import OptimizedImage from '../components/OptimizedImage';
import SEOHead from '../components/SEOHead';
import { imageConfig, altTexts } from '../utils/imageConfig';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="about">
      <SEOHead 
        title="About Us - Saner Consulting | 25+ Years Business Transformation Experience"
        description="Learn about Saner Consulting Limited - expert business transformation consultancy with 25+ years experience, global reach across 5+ countries, supporting £500m+ annual transformation investments."
        keywords="about saner consulting, business transformation experts, AI transformation consultancy, digital transformation experience, UK consulting"
        canonical="https://sanerconsulting.co.uk/about"
      />
      {/* Hero Section */}
      <section className="section">
        <div className="container">
          <h1 className="hero-title fade-in-up">About</h1>
          <p className="subtitle fade-in-up" style={{animationDelay: '0.2s'}}>
            Modernise technology. Transform capabilities. Deliver results.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="section section-dark">
        <div className="container">
          <div className="grid grid-2">
            <div>
              <h2 className="section-title">Our Story</h2>
              <p className="card-description">
                <strong>Saner Consulting Limited</strong> bridges traditional business with cutting-edge technology, bringing over two decades of digital transformation experience.
              </p>
              <p className="card-description">
                We believe that successful transformation goes beyond technology implementation – it requires a deep 
                understanding of people, processes, and organizational culture. Our approach focuses on delivering meaningful, 
                tangible change while avoiding "busy work and PowerPoint decks."
              </p>
              <div className="company-details">
                <h3 className="card-title">Company Information</h3>
                <ul className="company-info-list">
                  <li><strong>Company:</strong> Saner Consulting Limited</li>
                  <li><strong>Registration:</strong> England Number 10471658</li>
                  <li><strong>VAT Number:</strong> 255130531</li>
                  <li><strong>Registered Address:</strong> SG House, 6 St Cross Road, Winchester, Hampshire, SO23 9HX</li>
                </ul>
              </div>
            </div>
            <div>
              <h2 className="section-title">Our Mission</h2>
              <p className="card-description">
                To empower organizations with the strategies, tools, and capabilities needed to thrive in an 
                increasingly digital world, while maintaining their core values and human-centered approach. We specialize 
                in cutting through the AI hype to deliver transformations that generate real ROI.
              </p>
              <h3 className="card-title">Our Values</h3>
              <ul className="values-list">
                <li><strong>Results-Driven:</strong> From business case to benefits realized, we deliver measurable outcomes</li>
                <li><strong>Excellence:</strong> Delivering exceptional results that exceed expectations</li>
                <li><strong>Integrity:</strong> Building trust through transparency and ethical practices</li>
                <li><strong>Innovation:</strong> Leveraging AI and emerging technologies for competitive advantage</li>
              </ul>
              <h3 className="card-title">Geographic Coverage</h3>
              <p className="card-description">
                With extensive international experience across <strong>5+ countries</strong>, we bring global best practices 
                to local implementations across the UK, Europe, and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="section">
        <div className="container">
          <h2 className="section-title fade-in-up">Principal Consultant</h2>
          <div className="principal-consultant-section fade-in-up" style={{animationDelay: '0.2s'}}>
            <div className="consultant-profile">
              <div className="consultant-header">
                <div className="consultant-image">
                  <div style={{
                    width: '120px',
                    height: '120px',
                    overflow: 'hidden',
                    position: 'relative',
                    borderRadius: '50%',
                    border: '3px solid #dc2626'
                  }}>
                    <div style={{
                      height: '140px',
                      marginTop: '-10px'
                    }}>
                      <OptimizedImage
                        src={imageConfig.about.principalConsultant}
                        alt={altTexts.about.principalConsultant}
                        width="120px"
                        height="140px"
                        className="consultant-photo"
                      />
                    </div>
                  </div>
                </div>
                <div className="consultant-info">
                  <h3 className="card-title">Armando Sanchez</h3>
                  <p className="team-role">Principal Consultant & Founder</p>
                  <p className="consultant-subtitle">Business & Technology Transformation Leader</p>
                </div>
              </div>
              
              <div className="consultant-bio">
                <p className="card-description">
                  <strong>22+ years of experience</strong> leading business and technology transformation across global organizations. 
                  Specializes in portfolio management, program assurance, governance, and benefit realization with a proven track record 
                  of managing portfolios up to <strong>£500 million annually</strong>.
                </p>
                
                <div className="bio-section">
                  <h4>Business Transformation Expertise</h4>
                  <p className="card-description">
                    Beyond AI implementation, Armando brings extensive experience in <strong>end-to-end business transformation</strong> 
                    across multiple industries. His track record includes leading <strong>digital modernization initiatives</strong>, 
                    <strong>organizational restructuring programs</strong>, and <strong>process reengineering projects</strong> that 
                    have delivered measurable ROI improvements.
                  </p>
                  <ul className="bio-list">
                    <li><strong>Financial Services Transformation:</strong> Led digital modernization across HSBC, Barclays, and Morgan Stanley, transforming legacy systems and operational processes</li>
                    <li><strong>Large-Scale Change Programs:</strong> Managed organizational transformations with budgets exceeding £250M, focusing on cultural change and capability building</li>
                    <li><strong>Cross-Industry Expertise:</strong> Delivered transformations across Finance, Insurance, Government, and Technology sectors with tailored methodologies</li>
                    <li><strong>Operational Excellence:</strong> Implemented Six Sigma and process optimization initiatives resulting in 20-40% efficiency gains</li>
                    <li><strong>Regulatory & Compliance:</strong> Guided organizations through major regulatory changes including Basel III, GDPR, and industry-specific compliance transformations</li>
                    <li><strong>Global Program Delivery:</strong> Successfully executed transformation initiatives across UK, Europe, and emerging markets with diverse cultural and regulatory environments</li>
                  </ul>
                </div>
                
                <div className="bio-highlights">
                  <div className="bio-section">
                    <h4>Education & Credentials</h4>
                    <ul className="bio-list">
                      <li><strong>Master of Science in IT</strong> - Software Engineering Management, Carnegie Mellon University</li>
                      <li><strong>BSc Computer Science</strong> - Universidad de los Andes, Colombia</li>
                      <li><strong>Certifications:</strong> MoP Practitioner, MSP Practitioner, PRINCE2 Practitioner, Sony Six Sigma Black Belt</li>
                    </ul>
                  </div>
                  
                  <div className="bio-section">
                    <h4>International Experience</h4>
                    <p className="card-description">
                      Lived and worked across <strong>5 countries</strong>: UK (London), Belgium (Brussels), France (Paris), 
                      Spain (Madrid), and Colombia (Bogota), bringing truly global perspective to transformation challenges.
                    </p>
                  </div>
                  
                  <div className="bio-section">
                    <h4>AI & Technology Expertise</h4>
                    <p className="card-description">
                      Cutting-edge expertise in AI integration including <strong>Large Language Models (LLMs)</strong>, 
                      <strong>AI agents</strong>, <strong>RAG (Retrieval-Augmented Generation)</strong>, 
                      <strong>vector databases</strong>, and <strong>agentic workflows</strong>. Focuses on delivering 
                      smart automations that save time and money while cutting through AI hype.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Portfolio */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title fade-in-up">Our Clients & Projects</h2>
          <p className="subtitle fade-in-up" style={{animationDelay: '0.2s'}}>
            Trusted partners. Complex transformations. Proven delivery.
          </p>
          
          <div className="clients-grid animate-stagger">
            <div className="client-sector fade-in-up">
              <OptimizedImage
                src={imageConfig.clients.retailBanking}
                alt="Retail Banking Services"
                width="100%"
                height="180px"
                className="sector-image"
              />
              <h3 className="sector-title">Retail Banking</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>HSBC</h4>
                  <p>Global PMO Programme Standards Manager - £400M portfolio over 4 years</p>
                </div>
                <div className="client-item">
                  <h4>Barclays</h4>
                  <p>Business Change Assurance & Oversight, Internal Audit - £250M annual portfolio</p>
                </div>
              </div>
            </div>

            <div className="client-sector fade-in-up">
              <OptimizedImage
                src={imageConfig.clients.corporateBanking}
                alt="Corporate & Investment Banking"
                width="100%"
                height="180px"
                className="sector-image"
              />
              <h3 className="sector-title">Corporate & Investment Banking</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>Morgan Stanley</h4>
                  <p>AVP Technology Test Architect - Investment banking technology transformation</p>
                </div>
                <div className="client-item">
                  <h4>London Clearing House (LCH) / LSEG</h4>
                  <p>Change Portfolio & Programme Delivery Lead - Central counterparty clearing</p>
                </div>
              </div>
            </div>

            <div className="client-sector fade-in-up">
              <OptimizedImage
                src={imageConfig.clients.assetManagement}
                alt="Asset Management"
                width="100%"
                height="180px"
                className="sector-image"
              />
              <h3 className="sector-title">Asset Management</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>M&G Investments / Prudential</h4>
                  <p>Head of Portfolio Management Office & Strategic Transformation Lead - £250M portfolio</p>
                </div>
              </div>
            </div>

            <div className="client-sector fade-in-up">
              <OptimizedImage
                src={imageConfig.clients.insurance}
                alt="Insurance"
                width="100%"
                height="180px"
                className="sector-image"
              />
              <h3 className="sector-title">Insurance</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>Direct Line Group</h4>
                  <p>Senior Transformation Assurance Consultant - £200M annual transformation portfolio</p>
                </div>
              </div>
            </div>

            <div className="client-sector fade-in-up">
              <OptimizedImage
                src={imageConfig.clients.publicSector}
                alt={altTexts.clients.publicSector}
                width="100%"
                height="180px"
                className="sector-image"
              />
              <h3 className="sector-title">Public Sector & Government</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>UK Crown Prosecution Services (CPS)</h4>
                  <p>Head of Digital Portfolio Management & Benefit Realisation - Digital transformation</p>
                </div>
              </div>
            </div>

            <div className="client-sector fade-in-up">
              <OptimizedImage
                src={imageConfig.clients.software}
                alt="Software"
                width="100%"
                height="180px"
                className="sector-image"
              />
              <h3 className="sector-title">Software</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>Sony</h4>
                  <p>Software Quality Manager - European operations (Brussels)</p>
                </div>
              </div>
            </div>

            <div className="client-sector fade-in-up">
              <div style={{
                height: '180px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div style={{
                  height: '260px',
                  marginTop: '-10px',
                  objectPosition: 'center top'
                }}>
                  <OptimizedImage
                    src="/images/clients/consulting-services.jpg"
                    alt="Management Consulting"
                    width="100%"
                    height="240px"
                    className="sector-image"
                  />
                </div>
              </div>
              <h3 className="sector-title">Management Consulting</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>Accenture</h4>
                  <p>Senior Consultant Manager - Process & Test Management</p>
                </div>
              </div>
            </div>

            <div className="client-sector fade-in-up">
              <OptimizedImage
                src={imageConfig.clients.energySector}
                alt="Energy"
                width="100%"
                height="180px"
                className="sector-image"
              />
              <h3 className="sector-title">Energy</h3>
              <div className="client-list">
                <div className="client-item">
                  <h4>GE Capital</h4>
                  <p>EMEA Technology PMO Senior Manager - $40M annual portfolio</p>
                </div>
                <div className="client-item">
                  <h4>Schlumberger</h4>
                  <p>Global IT Quality Assurance Coordinator (Paris) - Oil & Gas services</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodologies & Best Practices */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Methodologies & Best Practices</h2>
          <p className="subtitle">
            Proven frameworks and methodologies deployed across complex transformation initiatives worldwide.
          </p>
          
          <div className="methodologies-grid">
            <div className="methodology-category">
              <h3 className="category-title">Portfolio & Programme Management</h3>
              <div className="methodology-items">
                <div className="methodology-item">
                  <h4>MoP (Management of Portfolios)</h4>
                  <p>Practitioner-level expertise in strategic portfolio alignment and prioritization</p>
                </div>
                <div className="methodology-item">
                  <h4>MSP (Managing Successful Programmes)</h4>
                  <p>End-to-end programme delivery and benefits realization frameworks</p>
                </div>
                <div className="methodology-item">
                  <h4>PRINCE2</h4>
                  <p>Structured project management with controlled environments and risk management</p>
                </div>
              </div>
            </div>
            
            <div className="methodology-category">
              <h3 className="category-title">Quality & Process Excellence</h3>
              <div className="methodology-items">
                <div className="methodology-item">
                  <h4>Six Sigma Black Belt</h4>
                  <p>Data-driven process improvement and defect reduction methodologies</p>
                </div>
                <div className="methodology-item">
                  <h4>CMMI (Capability Maturity Model)</h4>
                  <p>Software engineering process improvement and organizational maturity</p>
                </div>
                <div className="methodology-item">
                  <h4>Agile & SCRUM</h4>
                  <p>Iterative delivery with continuous feedback and adaptive planning</p>
                </div>
              </div>
            </div>
            
            <div className="methodology-category">
              <h3 className="category-title">Governance & Assurance</h3>
              <div className="methodology-items">
                <div className="methodology-item">
                  <h4>Risk-Based Approach (2LoD)</h4>
                  <p>Second Line of Defence oversight and challenge for transformation portfolios</p>
                </div>
                <div className="methodology-item">
                  <h4>Internal Audit (3LoD)</h4>
                  <p>Independent assurance and oversight of change delivery controls</p>
                </div>
                <div className="methodology-item">
                  <h4>Benefits Realization</h4>
                  <p>End-to-end tracking from business case to measurable outcomes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies & Tools */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">Technologies & Tools</h2>
          <div className="technologies-grid">
            <div className="tech-category">
              <h3 className="category-title">AI & Automation</h3>
              <div className="tech-list">
                <span className="tech-item">Large Language Models (LLMs)</span>
                <span className="tech-item">AI Agents</span>
                <span className="tech-item">RAG (Retrieval-Augmented Generation)</span>
                <span className="tech-item">Vector Databases</span>
                <span className="tech-item">Agentic Workflows</span>
                <span className="tech-item">Smart Automations</span>
              </div>
            </div>
            
            <div className="tech-category">
              <h3 className="category-title">Microsoft Stack</h3>
              <div className="tech-list">
                <span className="tech-item">Power Automate</span>
                <span className="tech-item">SharePoint</span>
                <span className="tech-item">Power BI</span>
                <span className="tech-item">MS Lists</span>
                <span className="tech-item">Excel Advanced</span>
              </div>
            </div>
            
            <div className="tech-category">
              <h3 className="category-title">Analytics & Reporting</h3>
              <div className="tech-list">
                <span className="tech-item">Looker Studio</span>
                <span className="tech-item">Data Visualization</span>
                <span className="tech-item">Performance Dashboards</span>
                <span className="tech-item">Executive Reporting</span>
              </div>
            </div>
            
            <div className="tech-category">
              <h3 className="category-title">Development & Web</h3>
              <div className="tech-list">
                <span className="tech-item">HTML</span>
                <span className="tech-item">CSS</span>
                <span className="tech-item">Web Technologies</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Our Track Record</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">22+</div>
              <div className="stat-label">Years of Experience</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">£500M</div>
              <div className="stat-label">Max Portfolio Size</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">18</div>
              <div className="stat-label">Major Transformation Roles</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">12</div>
              <div className="stat-label">Years Managing Teams</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">5</div>
              <div className="stat-label">Countries Experience</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">25</div>
              <div className="stat-label">Max Team Influence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy & Data Protection */}
      <section className="section">
        <div className="container">
          <h2 className="section-title fade-in-up">Privacy & Data Protection</h2>
          <div className="privacy-content fade-in-up" style={{animationDelay: '0.2s'}}>
            <div className="grid grid-2">
              <div className="privacy-section">
                <h3 className="card-title">Your Privacy Matters</h3>
                <p className="card-description">
                  Saner Consulting Limited is committed to protecting your privacy and complying with UK and EU data protection regulations, 
                  including GDPR and PECR. We believe in transparency about how we handle your information.
                </p>
              </div>
              
              <div className="privacy-section">
                <h3 className="card-title">No Tracking Policy</h3>
                <p className="card-description">
                  <strong>We do not use any tracking, analytics, or marketing cookies.</strong> Our website only uses essential 
                  cookies required for basic functionality like form submissions and remembering your cookie preferences.
                </p>
              </div>
              
              <div className="privacy-section">
                <h3 className="card-title">Data We Collect</h3>
                <ul className="privacy-list">
                  <li><strong>Contact Information:</strong> Only when you voluntarily provide it through our contact forms</li>
                  <li><strong>Technical Data:</strong> Basic server logs for security and functionality (IP addresses, browser type)</li>
                  <li><strong>Cookies:</strong> Only essential cookies for website functionality</li>
                </ul>
              </div>
              
              <div className="privacy-section">
                <h3 className="card-title">Your Rights</h3>
                <p className="card-description">
                  Under GDPR, you have the right to access, rectify, erase, restrict processing, data portability, 
                  and object to processing of your personal data. Contact us at{' '}
                  <strong>contact@ganerconsulting.co.uk</strong> to exercise these rights.
                </p>
              </div>
              
              <div className="privacy-section">
                <h3 className="card-title">Data Retention</h3>
                <p className="card-description">
                  We only retain personal data for as long as necessary to fulfill the purposes for which it was collected, 
                  typically no longer than 2 years for contact inquiries, unless legally required otherwise.
                </p>
              </div>
              
              <div className="privacy-section">
                <h3 className="card-title">Contact Our DPO</h3>
                <p className="card-description">
                  For privacy-related questions or to exercise your data protection rights, contact our Data Protection Officer at{' '}
                  <strong>contact@ganerconsulting.co.uk</strong> or write to our registered address.
                </p>
              </div>
            </div>
            
            <div className="legal-notice" style={{ marginTop: '3rem', opacity: '0.5', fontSize: '0.85rem', color: '#888888' }}>
              <h4 style={{ fontSize: '0.9rem', color: '#999999', marginBottom: '0.5rem' }}>Legal Compliance</h4>
              <p style={{ marginBottom: '0.5rem', lineHeight: '1.4' }}>
                This website complies with UK and EU privacy laws including the General Data Protection Regulation (GDPR) 
                and the Privacy and Electronic Communications Regulations (PECR). We are committed to maintaining the highest 
                standards of data protection and privacy.
              </p>
              <p style={{ fontSize: '0.8rem', color: '#777777' }}>
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-dark">
        <div className="container">
          <div className="cta-content">
            <h2 className="section-title">Ready to Work Together?</h2>
            <p className="subtitle">
              Ready to transform? Get in touch.
            </p>
            <Link to="/contact#contact-form" className="btn btn-primary">
              Start Your Transformation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;